import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Zap, Users, ArrowRight, Sparkles, Heart, Play, HelpCircle, Timer } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const faqData = [
  {
    question: "What happened to Stride?",
    answer: "Stride was discontinued on February 15, 2019 less than two years after launch, after Atlassian partnered with Slack. CSW offers free collaboration tools as an alternative."
  },
  {
    question: "Is CSW a free Stride alternative?",
    answer: "Yes, CSW is completely free with virtual collaboration rooms, task management, and productivity timers—no subscription required."
  },
  {
    question: "What features does CSW offer compared to Stride?",
    answer: "CSW offers 24/7 virtual rooms, customizable Pomodoro timers, Task Management with Kanban boards, and community features—all free."
  },
  {
    question: "Can I collaborate with teams on CSW like Stride?",
    answer: "Yes! CSW's Study Stream lets you join or host live collaboration sessions with people worldwide."
  },
  {
    question: "Does CSW have focus features like Stride?",
    answer: "Yes, CSW has focus modes and Pomodoro timers to help you concentrate without constant interruptions."
  },
  {
    question: "Is CSW better than Stride was?",
    answer: "CSW offers productivity-focused features including unlimited streams, task management, and themed study rooms—completely free."
  }
];

const Stride = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Stride Alternative - Free Team Collaboration"
        description="Looking for a Stride alternative? Stride shut down in 2019 but CSW offers free team collaboration with Study Stream, Study Timer, and Task Management."
        keywords="stride alternative, stride app, atlassian stride, team chat, slack alternative, team collaboration"
        canonicalUrl="/stride/"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Stride Alternative - Free Team Collaboration | CSW",
        "description": "Looking for a Stride alternative? Stride shut down in 2019 but CSW offers free team collaboration.",
        "url": "https://csw.live/stride/",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
        "about": { "@type": "Thing", "name": "Stride Alternative" },
        "keywords": "stride alternative, stride app, atlassian stride, team chat"
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Stride Alternative",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "1000" },
        "featureList": ["Free Collaboration", "24/7 Study Rooms", "Focus Mode", "Task Management", "Pomodoro Timer"],
        "publisher": { "@type": "Organization", "name": "CSW", "url": "https://csw.live" }
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } }))
      })}} />
      
      <Header />

      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        
        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-gradient">Stride</span>
            </h1>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Stride Alternative</h1>
            <h2 className="font-display text-2xl sm:text-3xl text-muted-foreground">Free Team Collaboration <span className="text-gradient">24/7</span></h2>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              When Stride shut down in 2019 after less than two years, teams lost a promising collaboration tool. CSW fills that void with free, unlimited sessions. Join our <Link to="/study-with-me/" className="text-primary hover:underline">productive community</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 px-8 py-6 text-lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Collaborating Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span>Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>No Limits</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-8 bg-card/50 border-primary/20">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Zap className="w-7 h-7 text-primary" />
              </div>
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">Stride Has <span className="text-gradient">Shut Down</span></h2>
                <p className="text-muted-foreground text-lg">
                  Stride—Atlassian's ambitious HipChat successor with built-in video, threaded discussions, and Focus Mode—was discontinued February 15, 2019. <Link to="/stride/history/" className="text-primary hover:underline font-medium">Read the full Stride story →</Link>
                </p>
                <p className="text-muted-foreground text-lg">
                  <span className="font-semibold text-foreground">CSW</span> offers a free alternative. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link> and <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tools</Link>.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">24/7 <span className="text-gradient">Collaboration Rooms</span></h2>
              <p className="text-muted-foreground text-lg">
                CSW's <Link to="/study-stream/" className="text-primary hover:underline">study rooms</Link> are always open. Work alongside peers from around the world anytime.
              </p>
            </div>
            <Card className="p-8 bg-card/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <Users className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-primary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Timer className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">Focus <span className="text-gradient">Mode</span></h2>
              <p className="text-muted-foreground text-lg">
                Like Stride's Focus feature, CSW helps you minimize distractions with <Link to="/study-timer/" className="text-primary hover:underline">customizable timers</Link> and structured work sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Frequently Asked <span className="text-gradient">Questions</span></h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg border px-6">
                  <AccordionTrigger className="text-left font-medium py-4">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to <span className="text-gradient">Get Started?</span></h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands who've made the switch to CSW—a free, stable platform built to last.
          </p>
          <Button size="lg" asChild>
            <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
              Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>

      <RelatedAlternatives currentPlatform="Stride" />

      <Footer />
    </div>
  );
};

export default Stride;
